import { maybe } from '@passionware/monads';
import {
  QueryClient,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import { resourceShortlistKeys } from 'v4/entities/associations/resource__shortlist/resource__shortlist.keys';
import { ListResponse } from 'v4/entities/common/common.crud.types';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import {
  ResourceShortlistAssociationQueryApi,
  UseResourceShortlistAssociationQueryApi
} from './http/resource__shortlist.http.query';
import {
  AssociationQuery,
  querySchemas,
  ShortlistBlock_normalized
} from './resource__shortlist.schemas';
import {
  resourceQueryUtils,
  ResourceSearchQuery
} from 'v5/api/resource/resource.api';

export function createListQueryHooks(
  useAssociationQueryApi: UseResourceShortlistAssociationQueryApi
) {
  const getListQueryOptions = (
    queryApi: ResourceShortlistAssociationQueryApi,
    query: AssociationQuery | undefined,
    client: QueryClient
  ) => ({
    queryKey: resourceShortlistKeys.list(query),
    queryFn: async (): Promise<
      ListResponse<ShortlistBlock_normalized, ResourceSearchQuery>
    > => {
      // Always use search path for now, as the list path relies on cache that may not be reliable
      // Check if this is a shortlist query with potential narrowing filters
      if (query && 'shortlist_id' in query) {
        const hasNarrowingFilters =
          query.query &&
          (!isEmpty(query.query.filters) ||
            !isEmpty(query.query.search) ||
            !isEmpty(query.query.sort) ||
            !isEmpty(query.query.filters?.archived));

        if (!hasNarrowingFilters) {
          // No narrowing filters, just return all blocks using default query
          const defaultQuery: AssociationQuery = {
            shortlist_id: query.shortlist_id,
            query: resourceQueryUtils.ofEmptySearchQuery()
          };
          return await queryApi.list(defaultQuery);
        }

        // we need to mimic search query where block search returns only resource ids,
        // so we first try to reuse something from the cache (no query) and fetch for the rest
        const queryWithoutFilterQuery: AssociationQuery = {
          shortlist_id: query.shortlist_id,
          query: resourceQueryUtils.ofEmptySearchQuery()
        };
        const [baseData, search] = await Promise.all([
          client
            .ensureQueryData({
              queryKey: resourceShortlistKeys.list(queryWithoutFilterQuery),
              queryFn: () => queryApi.list(queryWithoutFilterQuery)
            })
            .then(data =>
              parseWithDecoratedError(querySchemas.list.response, data)
            ),
          queryApi.search(query)
        ]);
        return {
          ...baseData,
          query: query.query!,
          results: search.results.map(
            id => baseData.results.find(block => block.contact_id === id)!
          )
        };
      }

      // Fallback for resource_id queries
      return await queryApi.list(query!);
    }
  });

  const useList = (
    query: AssociationQuery | undefined,
    options?: Pick<
      UseQueryOptions<
        ListResponse<ShortlistBlock_normalized, ResourceSearchQuery>,
        Error,
        ListResponse<ShortlistBlock_normalized, ResourceSearchQuery>
      >,
      'placeholderData'
    >
  ) => {
    const client = useQueryClient();
    const queryApi = useAssociationQueryApi();
    return useQuery({
      ...getListQueryOptions(queryApi, query, client),
      enabled: maybe.isPresent(query),
      ...options
    });
  };

  const useEnsureList = () => {
    const client = useQueryClient();
    const queryApi = useAssociationQueryApi();
    return async (query: AssociationQuery) =>
      client.ensureQueryData({
        ...getListQueryOptions(queryApi, query, client)
      });
  };

  return {
    useList,
    useEnsureList
  };
}
